import React, { useMemo } from "react";
import { Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * A two-column phone field: country dial code (with flag) + national number.
 * Combines into an E.164-style string ("+995591969901") via onChange.
 *
 * Props:
 *   value:       the combined E.164 string (or "")
 *   onChange:    (newValue: string) => void
 *   testId:      base data-testid (defaults to "phone-input")
 *   disabled:    boolean
 *   required:    boolean (visual only — actual validation lives in caller)
 *
 * Georgia (+995) is the default country. A short curated list of countries
 * covers Georgia, neighbouring CIS markets, and the most-common diaspora
 * destinations. Sorted with Georgia first, then alphabetical by name.
 */

// flag · dial · name (English) · ISO2
// NOTE: minimal curated list to keep bundle size small and the dropdown
//       scannable. Add new entries here when partners need them.
export const COUNTRY_CODES = [
  { iso: "GE", flag: "🇬🇪", dial: "+995", name: "Georgia" },
  { iso: "AM", flag: "🇦🇲", dial: "+374", name: "Armenia" },
  { iso: "AT", flag: "🇦🇹", dial: "+43",  name: "Austria" },
  { iso: "AZ", flag: "🇦🇿", dial: "+994", name: "Azerbaijan" },
  { iso: "BY", flag: "🇧🇾", dial: "+375", name: "Belarus" },
  { iso: "BE", flag: "🇧🇪", dial: "+32",  name: "Belgium" },
  { iso: "BG", flag: "🇧🇬", dial: "+359", name: "Bulgaria" },
  { iso: "CA", flag: "🇨🇦", dial: "+1",   name: "Canada" },
  { iso: "CN", flag: "🇨🇳", dial: "+86",  name: "China" },
  { iso: "HR", flag: "🇭🇷", dial: "+385", name: "Croatia" },
  { iso: "CY", flag: "🇨🇾", dial: "+357", name: "Cyprus" },
  { iso: "CZ", flag: "🇨🇿", dial: "+420", name: "Czechia" },
  { iso: "DK", flag: "🇩🇰", dial: "+45",  name: "Denmark" },
  { iso: "EE", flag: "🇪🇪", dial: "+372", name: "Estonia" },
  { iso: "FI", flag: "🇫🇮", dial: "+358", name: "Finland" },
  { iso: "FR", flag: "🇫🇷", dial: "+33",  name: "France" },
  { iso: "DE", flag: "🇩🇪", dial: "+49",  name: "Germany" },
  { iso: "GR", flag: "🇬🇷", dial: "+30",  name: "Greece" },
  { iso: "HU", flag: "🇭🇺", dial: "+36",  name: "Hungary" },
  { iso: "IN", flag: "🇮🇳", dial: "+91",  name: "India" },
  { iso: "IR", flag: "🇮🇷", dial: "+98",  name: "Iran" },
  { iso: "IE", flag: "🇮🇪", dial: "+353", name: "Ireland" },
  { iso: "IL", flag: "🇮🇱", dial: "+972", name: "Israel" },
  { iso: "IT", flag: "🇮🇹", dial: "+39",  name: "Italy" },
  { iso: "KZ", flag: "🇰🇿", dial: "+7",   name: "Kazakhstan" },
  { iso: "LV", flag: "🇱🇻", dial: "+371", name: "Latvia" },
  { iso: "LT", flag: "🇱🇹", dial: "+370", name: "Lithuania" },
  { iso: "LU", flag: "🇱🇺", dial: "+352", name: "Luxembourg" },
  { iso: "MT", flag: "🇲🇹", dial: "+356", name: "Malta" },
  { iso: "MD", flag: "🇲🇩", dial: "+373", name: "Moldova" },
  { iso: "NL", flag: "🇳🇱", dial: "+31",  name: "Netherlands" },
  { iso: "NO", flag: "🇳🇴", dial: "+47",  name: "Norway" },
  { iso: "PL", flag: "🇵🇱", dial: "+48",  name: "Poland" },
  { iso: "PT", flag: "🇵🇹", dial: "+351", name: "Portugal" },
  { iso: "RO", flag: "🇷🇴", dial: "+40",  name: "Romania" },
  { iso: "RU", flag: "🇷🇺", dial: "+7",   name: "Russia" },
  { iso: "SA", flag: "🇸🇦", dial: "+966", name: "Saudi Arabia" },
  { iso: "RS", flag: "🇷🇸", dial: "+381", name: "Serbia" },
  { iso: "SK", flag: "🇸🇰", dial: "+421", name: "Slovakia" },
  { iso: "SI", flag: "🇸🇮", dial: "+386", name: "Slovenia" },
  { iso: "ES", flag: "🇪🇸", dial: "+34",  name: "Spain" },
  { iso: "SE", flag: "🇸🇪", dial: "+46",  name: "Sweden" },
  { iso: "CH", flag: "🇨🇭", dial: "+41",  name: "Switzerland" },
  { iso: "TR", flag: "🇹🇷", dial: "+90",  name: "Turkey" },
  { iso: "UA", flag: "🇺🇦", dial: "+380", name: "Ukraine" },
  { iso: "AE", flag: "🇦🇪", dial: "+971", name: "UAE" },
  { iso: "GB", flag: "🇬🇧", dial: "+44",  name: "United Kingdom" },
  { iso: "US", flag: "🇺🇸", dial: "+1",   name: "United States" },
];

const DEFAULT_ISO = "GE";

// Split a stored E.164 value into { iso, national } using the longest dial
// match. The list above intentionally has unique dial codes per ISO key, so
// the longest-prefix winner is deterministic.
function splitValue(stored) {
  const cleaned = (stored || "").replace(/[^\d+]/g, "");
  if (!cleaned) return { iso: DEFAULT_ISO, national: "" };

  // Try longest-prefix dial match.
  const sorted = [...COUNTRY_CODES].sort(
    (a, b) => b.dial.length - a.dial.length,
  );
  for (const c of sorted) {
    if (cleaned.startsWith(c.dial)) {
      return { iso: c.iso, national: cleaned.slice(c.dial.length) };
    }
  }
  // Fallback: assume Georgia + drop leading + if any.
  return { iso: DEFAULT_ISO, national: cleaned.replace(/^\+/, "") };
}

export default function PhoneInput({
  value = "",
  onChange,
  testId = "phone-input",
  disabled = false,
  required = false,
}) {
  const { iso, national } = useMemo(() => splitValue(value), [value]);
  const selected =
    COUNTRY_CODES.find((c) => c.iso === iso) ||
    COUNTRY_CODES.find((c) => c.iso === DEFAULT_ISO);

  const emit = (newIso, newNational) => {
    const country = COUNTRY_CODES.find((c) => c.iso === newIso) || selected;
    const digits = (newNational || "").replace(/[^\d]/g, "");
    const combined = digits ? `${country.dial}${digits}` : "";
    onChange?.(combined);
  };

  return (
    <div className="flex gap-2" data-testid={testId}>
      <Select
        value={selected.iso}
        onValueChange={(iso2) => emit(iso2, national)}
        disabled={disabled}
      >
        <SelectTrigger
          className="h-11 w-[120px] shrink-0 rounded-xl"
          data-testid={`${testId}-country-trigger`}
          aria-label="Country dial code"
        >
          <SelectValue>
            <span className="flex items-center gap-1.5 text-sm">
              <span aria-hidden className="text-base leading-none">
                {selected.flag}
              </span>
              <span className="tabular-nums">{selected.dial}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72" data-testid={`${testId}-country-list`}>
          {COUNTRY_CODES.map((c) => (
            <SelectItem
              key={c.iso}
              value={c.iso}
              data-testid={`${testId}-country-${c.iso}`}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden className="text-base leading-none">
                  {c.flag}
                </span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-[hsl(var(--muted-foreground))] tabular-nums">
                  {c.dial}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative flex-1">
        <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
        <Input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={national}
          onChange={(e) => emit(selected.iso, e.target.value)}
          placeholder="591 96 99 01"
          disabled={disabled}
          required={required}
          aria-required={required || undefined}
          data-testid={`${testId}-number`}
          className="h-11 rounded-xl pl-10 tabular-nums"
        />
      </div>
    </div>
  );
}

// Lightweight validation helper used by callers (form-level)
export function isPhoneValid(stored) {
  const { national } = splitValue(stored);
  // Most national subscriber numbers are 6-15 digits per ITU E.164.
  return /^\d{6,15}$/.test(national);
}
